import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  MessageCircle,
  Mail,
  Save,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { toast } from "sonner";
import {
  getDiagnostic,
  updateDiagnostic,
  options as fetchOptions,
  publicReportUrl,
} from "@/lib/adminApi";

const PILLARS = [
  { id: "lideranca", name: "Liderança" },
  { id: "comunicacao", name: "Comunicação" },
  { id: "desenvolvimento_humano", name: "Desenvolvimento Humano" },
  { id: "gestao", name: "Gestão" },
  { id: "cultura", name: "Cultura" },
  { id: "performance", name: "Performance" },
  { id: "inovacao_crescimento", name: "Inovação e Crescimento" },
];

const ACTIONS = {
  lideranca: [
    "Estabelecer rotinas periódicas de acompanhamento entre líderes e equipes.",
    "Definir claramente papéis, responsabilidades e níveis de decisão.",
    "Desenvolver continuamente as competências de liderança dos gestores.",
  ],
  comunicacao: [
    "Padronizar os principais fluxos de comunicação interna.",
    "Realizar reuniões periódicas com objetivos e responsáveis definidos.",
    "Garantir que metas, prioridades e resultados sejam comunicados de forma clara.",
  ],
  desenvolvimento_humano: [
    "Estruturar planos de desenvolvimento alinhados às necessidades da empresa.",
    "Padronizar os processos de integração e capacitação dos colaboradores.",
    "Criar uma rotina contínua de desenvolvimento das competências da equipe.",
  ],
  gestao: [
    "Padronizar indicadores e rotinas de acompanhamento.",
    "Definir metas claras para equipes e lideranças.",
    "Implantar ciclos regulares de análise e tomada de decisão.",
  ],
  cultura: [
    "Reforçar os valores organizacionais nas práticas do dia a dia.",
    "Reconhecer comportamentos alinhados à cultura desejada.",
    "Medir periodicamente o clima e o engajamento das equipes.",
  ],
  performance: [
    "Acompanhar indicadores-chave de desempenho com frequência.",
    "Eliminar desperdícios e gargalos operacionais identificados.",
    "Promover ciclos contínuos de melhoria dos processos.",
  ],
  inovacao_crescimento: [
    "Incentivar a geração de ideias para melhoria dos processos.",
    "Avaliar regularmente oportunidades de inovação e expansão.",
    "Transformar aprendizados em melhorias permanentes para a empresa.",
  ],
};

const classify = (s) => {
  if (s >= 80) return { label: "Excelente", color: "#16A34A" };
  if (s >= 60) return { label: "Bom", color: "#65A30D" };
  if (s >= 40) return { label: "Atenção", color: "#FACC15" };
  return { label: "Crítico", color: "#DC2626" };
};

const onlyDigits = (v) => (v || "").replace(/\D/g, "");

export default function AdminDiagnosticDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [opts, setOpts] = useState({ status_comercial: [] });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [obs, setObs] = useState("");

  useEffect(() => {
    getDiagnostic(id)
      .then((doc) => {
        setD(doc);
        setObs(doc.observacoes || "");
      })
      .catch(() => toast.error("Diagnóstico não encontrado"));
    fetchOptions().then(setOpts).catch(() => {});
  }, [id]);

  const save = useCallback(async (patch) => {
    setSaving(true);
    try {
      const updated = await updateDiagnostic(id, patch);
      setD(updated);
      setSavedAt(new Date());
    } catch (e) {
      toast.error("Erro ao salvar");
    } finally {
      setSaving(false);
    }
  }, [id]);

  // Auto-save observações (debounced)
  useEffect(() => {
    if (!d) return;
    if (obs === (d.observacoes || "")) return;
    const t = setTimeout(() => {
      save({ observacoes: obs });
    }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [obs]);

  const analysis = useMemo(() => {
    if (!d) return null;
    const arr = PILLARS.map((p) => ({
      ...p,
      score: d.pillar_scores?.[p.id] || 0,
    })).sort((a, b) => a.score - b.score);
    return {
      strongest: arr[arr.length - 1],
      weakest: arr[0],
      priorities: arr.slice(0, 3),
    };
  }, [d]);

  if (!d) {
    return (
      <div className="text-slate-500 flex items-center gap-2 text-sm">
        <Loader2 className="animate-spin" size={16} /> Carregando…
      </div>
    );
  }

  const totalClass = classify(d.total_score);

  const waNumber = onlyDigits(d.company.telefone);
  const waUrl = waNumber
    ? `https://wa.me/${waNumber.length <= 11 ? "55" + waNumber : waNumber}`
    : null;
  const mailUrl = `mailto:${d.company.email}?subject=${encodeURIComponent(
    "Diagnóstico de Desenvolvimento Empresarial HUB — " + d.company.empresa
  )}`;

  const radarData = PILLARS.map((p) => ({
    pillar: p.name.split(" ").slice(0, 2).join(" "),
    fullName: p.name,
    score: d.pillar_scores?.[p.id] || 0,
  }));

  return (
    <div data-testid="admin-diagnostic-detail">
      {/* Back + header */}
      <button
        onClick={() => nav(-1)}
        className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1"
        data-testid="btn-back-list"
      >
        <ArrowLeft size={14} /> Voltar
      </button>

      <div className="mt-4 flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <div className="hub-eyebrow">Diagnóstico</div>
          <h1 className="mt-2 font-display font-medium text-3xl sm:text-4xl tracking-tight">
            {d.company.empresa}
          </h1>
          <div className="mt-2 text-sm text-slate-600">
            {d.company.nome} — {d.company.cargo} ·{" "}
            {new Date(d.created_at).toLocaleString("pt-BR")}
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <a
            href={publicReportUrl(d.id)}
            target="_blank"
            rel="noreferrer"
            className="hub-btn-primary"
            data-testid="btn-download-pdf"
          >
            <Download size={14} /> Baixar PDF
          </a>
          {waUrl && (
            <a
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              className="hub-btn-secondary"
              data-testid="btn-whatsapp"
            >
              <MessageCircle size={14} /> WhatsApp
            </a>
          )}
          <a
            href={mailUrl}
            className="hub-btn-secondary"
            data-testid="btn-email"
          >
            <Mail size={14} /> E-mail
          </a>
        </div>
      </div>

      {/* Score + Company */}
      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="hub-card p-6">
          <div className="hub-eyebrow">Índice HUB</div>
          <div className="mt-2 flex items-baseline gap-1">
            <div className="font-display font-medium text-6xl tracking-tight">
              {Math.round(d.total_score)}
            </div>
            <div className="text-slate-400 text-xl">/100</div>
          </div>
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0F172A] text-white text-sm">
            {d.badge}
          </div>
          <div className="mt-3 text-sm" style={{ color: totalClass.color }}>
            Classificação global: <b>{totalClass.label}</b>
          </div>
        </div>

        <div className="hub-card p-6 lg:col-span-2">
          <div className="hub-eyebrow">Dados da empresa</div>
          <div className="mt-4 grid sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
            <Info label="Empresa" value={d.company.empresa} />
            <Info label="Responsável" value={`${d.company.nome} — ${d.company.cargo}`} />
            <Info label="E-mail" value={d.company.email} />
            <Info label="Telefone" value={d.company.telefone || "—"} />
            <Info label="Cidade / UF" value={`${d.company.cidade} / ${d.company.estado}`} />
            <Info label="Segmento" value={d.company.segmento} />
            <Info label="Colaboradores" value={d.company.colaboradores} />
            <Info
              label="Data do diagnóstico"
              value={new Date(d.created_at).toLocaleString("pt-BR")}
            />
          </div>
        </div>
      </div>

      {/* Admin fields */}
      <div className="mt-6 hub-card p-6">
        <div className="hub-eyebrow">Gestão comercial</div>
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2">
              Status Comercial
            </span>
            <select
              data-testid="detail-status"
              className="hub-input"
              value={d.status_comercial || "Novo Diagnóstico"}
              onChange={(e) => save({ status_comercial: e.target.value })}
            >
              {(opts.status_comercial || []).map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2">
              Sessão Estratégica realizada?
            </span>
            <div className="flex gap-2">
              <button
                data-testid="detail-sessao-sim"
                type="button"
                onClick={() => save({ sessao_estrategica: true })}
                className={`flex-1 h-11 rounded-xl border transition-colors ${
                  d.sessao_estrategica
                    ? "bg-[#16A34A] border-[#16A34A] text-white"
                    : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                }`}
              >
                Sim
              </button>
              <button
                data-testid="detail-sessao-nao"
                type="button"
                onClick={() => save({ sessao_estrategica: false })}
                className={`flex-1 h-11 rounded-xl border transition-colors ${
                  !d.sessao_estrategica
                    ? "bg-[#0F172A] border-[#0F172A] text-white"
                    : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                }`}
              >
                Não
              </button>
            </div>
          </label>
          <div className="text-xs text-slate-500 self-end pb-1">
            {saving ? (
              <span className="inline-flex items-center gap-1">
                <Loader2 size={12} className="animate-spin" /> Salvando…
              </span>
            ) : savedAt ? (
              <span className="inline-flex items-center gap-1 text-[#16A34A]">
                <CheckCircle2 size={12} /> Salvo às{" "}
                {savedAt.toLocaleTimeString("pt-BR").slice(0, 5)}
              </span>
            ) : (
              <span>Alterações são salvas automaticamente.</span>
            )}
          </div>
        </div>
        <label className="block mt-6">
          <span className="block text-xs uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2">
            Observações internas
          </span>
          <textarea
            data-testid="detail-observacoes"
            className="hub-input min-h-32"
            placeholder="Ex.: Empresa interessada no HUB Academy. Enviar proposta. Ligar novamente em 15 dias."
            value={obs}
            onChange={(e) => setObs(e.target.value)}
          />
          <span className="mt-1 block text-xs text-slate-500">
            Salvo automaticamente enquanto você digita.
          </span>
        </label>
      </div>

      {/* Radar + Pillars */}
      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="hub-card p-6">
          <div className="hub-eyebrow">Perfil por pilar</div>
          <h3 className="mt-2 font-display font-medium text-lg">Radar de maturidade</h3>
          <div className="mt-4 h-80" data-testid="detail-radar">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} outerRadius="72%">
                <PolarGrid stroke="#E2E8F0" />
                <PolarAngleAxis dataKey="pillar" tick={{ fill: "#475569", fontSize: 11 }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: "#94A3B8", fontSize: 10 }} />
                <Radar dataKey="score" stroke="#0F172A" fill="#0F172A" fillOpacity={0.15} strokeWidth={2} />
                <Tooltip formatter={(v) => [`${Math.round(v)}%`, "Score"]} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="hub-card p-6">
          <div className="hub-eyebrow">Pontuação por pilar</div>
          <div className="mt-4 space-y-3">
            {PILLARS.map((p) => {
              const s = d.pillar_scores?.[p.id] || 0;
              const c = classify(s);
              return (
                <div key={p.id} className="flex items-center gap-3">
                  <div className="w-56 text-sm text-slate-700">{p.name}</div>
                  <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      style={{ width: `${s}%`, background: c.color }}
                      className="h-full rounded-full"
                    />
                  </div>
                  <div
                    className="w-12 text-right text-sm font-mono"
                    style={{ color: c.color }}
                  >
                    {Math.round(s)}%
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Summary + Priorities */}
      {analysis && (
        <div className="mt-6 grid lg:grid-cols-3 gap-6">
          <div className="hub-card p-6">
            <div className="hub-eyebrow" style={{ color: "#16A34A" }}>
              Ponto forte principal
            </div>
            <div className="mt-3 font-display font-medium text-xl">
              {analysis.strongest.name}
            </div>
            <div className="mt-1 text-sm text-slate-500">
              {Math.round(analysis.strongest.score)}% ·{" "}
              {classify(analysis.strongest.score).label}
            </div>
          </div>
          <div className="hub-card p-6">
            <div className="hub-eyebrow" style={{ color: "#DC2626" }}>
              Principal ponto de atenção
            </div>
            <div className="mt-3 font-display font-medium text-xl">
              {analysis.weakest.name}
            </div>
            <div className="mt-1 text-sm text-slate-500">
              {Math.round(analysis.weakest.score)}% ·{" "}
              {classify(analysis.weakest.score).label}
            </div>
          </div>
          <div className="hub-card p-6">
            <div className="hub-eyebrow">Prioridades</div>
            <ol className="mt-3 space-y-2 text-sm">
              {analysis.priorities.map((p, i) => (
                <li key={p.id} className="flex justify-between gap-2">
                  <span className="text-slate-700">
                    {i + 1}. {p.name}
                  </span>
                  <span
                    className="font-mono text-xs"
                    style={{ color: classify(p.score).color }}
                  >
                    {Math.round(p.score)}%
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}

      {/* Action plan */}
      {analysis && (
        <div className="mt-6">
          <div className="hub-eyebrow">Plano de Desenvolvimento</div>
          <h3 className="mt-2 font-display font-medium text-xl tracking-tight">
            Três prioridades sugeridas
          </h3>
          <div className="mt-5 grid md:grid-cols-3 gap-4">
            {analysis.priorities.map((p, i) => (
              <div key={p.id} className="hub-card p-5">
                <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#D97706]">
                  Prioridade {i + 1}
                </div>
                <div className="mt-1 font-display font-medium">{p.name}</div>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  {(ACTIONS[p.id] || []).map((a, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#D97706] flex-shrink-0" />
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8">
        <Link to="/admin/diagnosticos" className="text-sm text-slate-500 hover:text-slate-900">
          ← Voltar para lista
        </Link>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">
        {label}
      </div>
      <div className="mt-0.5 text-[#0F172A]">{value}</div>
    </div>
  );
}
